import { useEffect, useRef } from "react";
import MessageBlocks from "./MessageBlocks";
import LucasMarkdown from "./LucasMarkdown";
import AnswerFeedback from "./AnswerFeedback";
import ReplyQuote from "./ReplyQuote";
import MessageAttachments from "./MessageAttachments";
/**
 * O MESMO componente de ações das mensagens do Inbox — uma segunda barra
 * divergiria da de lá no primeiro ajuste que alguém fizesse.
 *
 * Importado direto do arquivo, e não do barril: o `index.js` reexporta a pilha
 * inteira de balões (WhatsApp, Instagram, TikTok e conteúdos), que esta tela
 * não usa. Pelo barril, a home carregaria o Inbox junto — e o teste nem chega a
 * rodar.
 */
import { MessageActions } from "@/pages/internal/inbox/components/messages/bubbles/shared/MessageActions.jsx";
import { BTN_SOLID, CHIP_TALL } from "../homeTokens";


/**
 * The conversation — laid out on the page, not inside a panel.
 *
 * No card, no border, no header bar. A framed box inside a page that is already
 * a surface reads as a widget bolted onto the screen; the conversation *is* the
 * screen once it opens, so it sits directly on the background. The assistant's
 * turns have no bubble either — a bubble is for distinguishing two speakers in
 * a cramped column, and here the alignment plus the user's dark pill already
 * does that. Long technical answers are far easier to read as plain text.
 *
 * It owns no input: the composer lives once in the page tree and is shared with
 * the closed state, so sending a question never remounts it.
 *
 * "Isso resolveu" is wired to `closeConversation` on purpose — that endpoint
 * generates the conversation summary, and until now nothing in the app ever
 * called it, so every past conversation shows "Sem resumo" in the history.
 */
export default function LucasChat({
  messages,
  isTyping,
  isLoading,
  activity,
  onResolve,
  onHandoff,
  onRequestHuman,
  onPickOption,
  onRate,
  onReply,
  onReact,
  onNavigate,
  handover = null,
  readOnly = false,
}) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, isTyping]);

  const last = messages[messages.length - 1];
  // Termina em "?" → o Lucas está esperando uma resposta, não oferecendo
  // encerramento. Heurística simples de propósito: pergunta em português
  // termina com ponto de interrogação, e errar para menos (não mostrar os
  // botões) custa um clique a mais; errar para mais interrompe a conversa.
  const lucasIsAsking =
    last?.role === "assistant" && /\?\s*$/.test(last.content || "");

  /**
   * Com uma pessoa atendendo, a conversa não oferece encerramento.
   *
   * "Isso resolveu" fecha a conversa e limpa a tela — em cima de um atendimento
   * em andamento, apagaria de vista justamente quem está esperando resposta. O
   * fim é decidido pelo agente, no Inbox.
   */
  const humanIsAnswering = !!handover?.active;

  const showClosingActions =
    messages.length > 0 && !isTyping && !isLoading && !readOnly && !humanIsAnswering;

  // Falar com uma pessoa é conquistado, e por turno. Quem decide é o backend —
  // ele só manda `handoff` quando constatou bloqueio no envio ou quando o
  // assunto é comercial. O front não adivinha, então o botão não tem como
  // voltar a ser permanente.
  //
  // `primary` separa socorro de conversa: com o envio bloqueado nada foi
  // resolvido, e oferecer "Isso resolveu" ao lado seria cinismo.
  const handoff = last?.role === "assistant" ? last.handoff : null;

  /**
   * A pergunta aberta esconde o "Isso resolveu", nunca o handoff.
   *
   * A regra nasceu certa — "Isso resolveu" ao lado de "me conta o que
   * aconteceu?" é um convite a abandonar a conversa no meio. Só que ela passou
   * a esconder também o botão de contratar, e a oferta termina exatamente em
   * "Quer seguir?": o botão que ela escondia era a RESPOSTA para a pergunta.
   *
   * São coisas diferentes. Encerrar no meio de uma pergunta é ruim; responder a
   * uma pergunta com um clique é o ponto.
   */
  const showResolve = !handoff?.primary && !lucasIsAsking;

  return (
    // O container de rolagem ocupa a largura toda da página, e a coluna de
    // leitura fica centralizada DENTRO dele. É isso que põe a barra na borda
    // direita da janela em vez de colada no texto.
    <div
      ref={scrollRef}
      className="custom-scrollbar h-full overflow-y-auto text-left"
    >
      <div className="mx-auto flex w-full max-w-[760px] flex-col gap-6 px-6 pb-6">
      {/* Quem está do outro lado mudou, e a tela precisa dizer. Sem isto, a
          pessoa continuaria escrevendo para o robô sem saber que já tem gente
          lendo — e leria a resposta de uma pessoa como se fosse do modelo. */}
      {humanIsAnswering && (
        <div className="sticky top-0 z-10 -mb-2 flex items-center gap-2 rounded-[12px] border border-[#1d84d6]/30 bg-[#1d84d6]/[.08] px-3.5 py-2 text-[12.5px] font-medium text-[#175f9c] backdrop-blur-md dark:border-[#84caff]/30 dark:bg-[#84caff]/[.10] dark:text-[#9fd4ff]">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#1d84d6] dark:bg-[#84caff]" />
          <span>
            Você está falando com nosso time
            {handover.ticketNumber ? ` · atendimento #${handover.ticketNumber}` : ""}
          </span>
        </div>
      )}

      {messages.map((message) => {
        /**
         * Aviso de sistema não tem autor.
         *
         * "Atendimento encerrado" não é fala do Lucas nem de uma pessoa —
         * desenhá-lo como turno faria a plataforma parecer estar falando, e
         * ainda ofereceria avaliar uma "resposta" que não é resposta de
         * ninguém. Fica centralizado e discreto, como divisor.
         */
        if (message.role === "system") {
          return (
            <div
              key={message.id}
              data-role="system-notice"
              className="mx-auto max-w-[560px] rounded-[10px] bg-black/[.05] px-3 py-1.5 text-center text-[12px] leading-[1.45] text-[#5c5f70] dark:bg-white/[.07] dark:text-white/55"
            >
              {message.content}
            </div>
          );
        }

        const isUser = message.role === "user";
        return (
          <div
            key={message.id}
            className={`flex flex-col gap-2 ${isUser ? "items-end" : "items-start"}`}
          >
            {/* Os MESMOS renderizadores do Inbox: lightbox com zoom e download
                para imagem, player com waveform para áudio, cartão com ícone e
                download para documento. A versão feita à mão que existia aqui
                mostrava o nome do arquivo e mais nada — documento que não
                baixava, imagem que não abria. */}
            {message.attachments?.length > 0 && (
              <div
                className={`flex w-full flex-col gap-1.5 ${
                  isUser ? "items-end" : "items-start"
                }`}
              >
                <MessageAttachments attachments={message.attachments} />
              </div>
            )}

            {message.content &&
              (isUser ? (
                <div className="flex max-w-[76%] flex-col gap-1.5 rounded-[18px] bg-[#15161f] px-4 py-2.5 text-[14px] font-medium leading-[1.5] text-white dark:bg-white dark:text-[#0a0a0f]">
                  {/* A citação também na fala dela: sem isso, o agente vê a
                      resposta com contexto e ela vê a própria frase solta. */}
                  {message.replyTo && (
                    // Dentro da bolha escura da pessoa (clara no tema escuro),
                    // a citação precisa contrastar com o PRÓPRIO fundo dela —
                    // a primeira versão sumia nos dois temas.
                    <span className="block rounded-[8px] border-l-[3px] border-white/60 bg-white/[.16] px-2 py-1 text-[12px] font-normal leading-[1.35] text-white/90 dark:border-black/45 dark:bg-black/[.10] dark:text-[#0a0a0f]/85">
                      {message.replyTo.content}
                    </span>
                  )}
                  {message.content}
                </div>
              ) : (
                // Superfície discreta com borda esquerda de acento: dá começo e
                // autoria ao turno sem virar o balão pesado de novo, e sem
                // precisar de avatar ocupando espaço em toda mensagem.
                //
                // A resposta de uma PESSOA muda o acento e ganha assinatura: sem
                // isso ela chegaria pintada de Lucas, e a pessoa não teria como
                // saber que já está falando com alguém do time.
                /**
                 * A caixa acompanha o conteúdo, não a coluna.
                 *
                 * Ocupar a largura inteira para responder "opa" deixava um
                 * retângulo enorme com três letras dentro. Só o que precisa de
                 * espaço — vídeo, imagem, cartão, trecho de fluxo — cresce até
                 * o fim; texto simples para onde termina.
                 */
                <div
                  className={`group relative flex min-w-0 flex-col gap-3 rounded-[14px] rounded-tl-[4px] border-l-2 px-4 py-3 ${
                    message.blocks?.length ? "w-full" : "w-fit max-w-[88%]"
                  } ${
                    message.role === "agent"
                      ? "border-[#1d84d6]/50 bg-[#1d84d6]/[.06] dark:border-[#84caff]/50 dark:bg-[#84caff]/[.08]"
                      : "border-[#12a06c]/35 bg-black/[.028] dark:border-[#7ee2a8]/35 dark:bg-white/[.045]"
                  }`}
                >
                  {/* Quem atende não se identifica: para quem lê, do outro
                      lado está a TouchWorks, não uma pessoa específica. A
                      faixa no topo já diz que é gente, e a cor separa do
                      Lucas — nome individual só exporia quem atendeu. */}

                  {/* Responder só existe onde há ticket para citar: com o
                      atendimento ativo, e sobre uma mensagem já gravada no
                      Inbox — é o id de LÁ que a citação exige. O componente é
                      o mesmo das mensagens do Inbox, e aparece no hover. */}
                  {!readOnly && onReply && message.inboxMessageId && (
                    <MessageActions
                      className="absolute right-2 top-2 z-10"
                      showSave={false}
                      copyText={message.content}
                      onReact={(emoji) => onReact?.(message.inboxMessageId, emoji)}
                      onReply={() =>
                        onReply({
                          id: message.inboxMessageId,
                          content: message.content,
                          fromAgent: true,
                        })
                      }
                    />
                  )}
                  {/* O trecho citado vem ANTES da fala — é o que dá sujeito a
                      um "isso aqui". */}
                  <ReplyQuote replyTo={message.replyTo} />
                  {/* O modelo escreve markdown, e o reescritor de nomes envolve
                      cada nó em `**`. Sem interpretar a marcação, o asterisco
                      chegava à tela. */}
                  <LucasMarkdown content={message.content} />
                  {/* Vídeo, imagem, cartão de decisão, trecho e download vivem
                      dentro do turno — clicar numa opção manda a mensagem que
                      ela carrega, continuando a mesma conversa. */}
                  <MessageBlocks blocks={message.blocks} onPick={onPickOption} onNavigate={onNavigate} />
                  {/* Avaliar exige a resposta GRAVADA: a avaliação tem a
                      mensagem como chave, e `messageId` só existe depois que
                      ela foi persistida. As saídas de erro do agente não
                      gravam nada — e não há o que avaliar nelas. */}
                  {(readOnly ? message.feedback : message.messageId) && (
                    <AnswerFeedback
                      value={message.feedback}
                      onRate={(vote) => onRate?.(message.messageId, vote)}
                      readOnly={readOnly}
                    />
                  )}

                  {/* A reação fica colada no rodapé da mensagem, como no
                      Inbox. Clicar na própria remove — reagir de novo
                      substitui, então não há como empilhar emoji. */}
                  {message.reactions?.length > 0 && (
                    <div className="-mb-1 flex flex-wrap items-center gap-1">
                      {message.reactions.map((reaction, index) => (
                        <button
                          key={reaction.id || index}
                          type="button"
                          disabled={readOnly || !reaction.fromContact}
                          onClick={() => onReact?.(message.inboxMessageId, "")}
                          title={reaction.fromContact ? "Clique para remover" : "Do nosso time"}
                          className="rounded-full bg-black/[.06] px-1.5 py-0.5 text-[13px] leading-none disabled:cursor-default dark:bg-white/[.10]"
                        >
                          {reaction.emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        );
      })}

      {(isTyping || isLoading) && (
        // Mesma superfície do turno pronto: os pontinhos nascem exatamente onde
        // a resposta vai nascer, então "pensando" → "respondido" não move nada.
        <div className="flex w-full items-center gap-2.5 rounded-[14px] rounded-tl-[4px] border-l-2 border-[#12a06c]/35 bg-black/[.028] px-4 py-4 dark:border-[#7ee2a8]/35 dark:bg-white/[.045]">
          <span className="flex items-center gap-1.5">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="h-1.5 w-1.5 rounded-full bg-black/35 dark:bg-white/45 animate-breathe motion-reduce:animate-none"
                style={{ animationDelay: `${dot * 0.18}s` }}
              />
            ))}
          </span>
          {/* Diz o que ele está fazendo. Uma análise profunda leva vários
              segundos, e três pontinhos parados são indistinguíveis de travado. */}
          {activity && (
            <span className="text-[13px] leading-none text-[#5c5f70] dark:text-white/55">
              {activity}
            </span>
          )}
        </div>
      )}

      {/* Enquanto o Lucas PERGUNTA — "me conta o que aconteceu?" — o "Isso
          resolveu" some: ao lado de uma pergunta aberta ele é um convite a
          abandonar a conversa no meio. O handoff NÃO some junto, porque a
          oferta termina em "Quer seguir?" e o botão é a resposta.
          Conversa encerrada não oferece nada: fecharia a ATIVA, que é outra. */}
      {showClosingActions && (showResolve || handoff) && (
        <div className="flex flex-wrap gap-2.5">
          {showResolve && (
            <button type="button" onClick={onResolve} className={BTN_SOLID}>
              Isso resolveu
            </button>
          )}
          {handoff && (
            <button
              type="button"
              // Para onde o clique leva é decidido pelo backend, nunca aqui:
              // `inbox` chama uma pessoa de verdade, `chat` continua o turno,
              // que é como o pedido comercial é registrado hoje.
              onClick={() =>
                handoff.via === "inbox"
                  ? onRequestHuman?.(handoff)
                  : onHandoff?.(handoff.message)
              }
              className={handoff.primary ? BTN_SOLID : CHIP_TALL}
            >
              {handoff.label}
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
